import { useState } from "react";
import { Avatar, Collapse } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useIntl } from "react-intl";
import StyledText from "./StyledText";
import HorizontalGraph from "./HorizontalGraph";
import goal1 from "../assets/goal1.png";
import goal2 from "../assets/goal2.png";
import goal3 from "../assets/goal3.png";
import goal4 from "../assets/goal4.png";
import goal5 from "../assets/goal5.png";
import goal6 from "../assets/goal6.png";
import goal7 from "../assets/goal7.png";
import goal8 from "../assets/goal8.png";
import goal9 from "../assets/goal9.png";
import goal10 from "../assets/goal10.png";
import goal11 from "../assets/goal11.png";
import goal12 from "../assets/goal12.png";
import goal13 from "../assets/goal13.png";
import goal14 from "../assets/goal14.png";
import goal15 from "../assets/goal15.png";
import goal16 from "../assets/goal16.png";
import goal17 from "../assets/goal17.png";
import goalInverted1 from "../assets/goalInverted1.png";
import goalInverted2 from "../assets/goalInverted2.png";
import goalInverted3 from "../assets/goalInverted3.png";
import goalInverted4 from "../assets/goalInverted4.png";
import goalInverted5 from "../assets/goalInverted5.png";
import goalInverted6 from "../assets/goalInverted6.png";
import goalInverted7 from "../assets/goalInverted7.png";
import goalInverted8 from "../assets/goalInverted8.png";
import goalInverted9 from "../assets/goalInverted9.png";
import goalInverted10 from "../assets/goalInverted10.png";
import goalInverted11 from "../assets/goalInverted11.png";
import goalInverted12 from "../assets/goalInverted12.png";
import goalInverted13 from "../assets/goalInverted13.png";
import goalInverted14 from "../assets/goalInverted14.png";
import goalInverted15 from "../assets/goalInverted15.png";
import goalInverted16 from "../assets/goalInverted16.png";
import goalInverted17 from "../assets/goalInverted17.png";

const goals = [
    { key: 1, image: goalInverted1, selected: goal1 },
    { key: 2, image: goalInverted2, selected: goal2 },
    { key: 3, image: goalInverted3, selected: goal3 },
    { key: 4, image: goalInverted4, selected: goal4 },
    { key: 5, image: goalInverted5, selected: goal5 },
    { key: 6, image: goalInverted6, selected: goal6 },
    { key: 7, image: goalInverted7, selected: goal7 },
    { key: 8, image: goalInverted8, selected: goal8 },
    { key: 9, image: goalInverted9, selected: goal9 },
    { key: 10, image: goalInverted10, selected: goal10 },
    { key: 11, image: goalInverted11, selected: goal11 },
    { key: 12, image: goalInverted12, selected: goal12 },
    { key: 13, image: goalInverted13, selected: goal13 },
    { key: 14, image: goalInverted14, selected: goal14 },
    { key: 15, image: goalInverted15, selected: goal15 },
    { key: 16, image: goalInverted16, selected: goal16 },
    { key: 17, image: goalInverted17, selected: goal17 },
];

const getMaxCount = (impactAlignment) => {
    if (!Array.isArray(impactAlignment)) return 1;
    let maxCount = 0;
    const findCounts = (items) => {
        items.forEach(item => {
            if (typeof item.count === 'number') {
                maxCount = Math.max(maxCount, item.count);
            }
            if (Array.isArray(item.outcomes)) {
                findCounts(item.outcomes);
            }
        });
    };

    findCounts(impactAlignment);
    return maxCount || 1;
};

const processImpactAlignment = (impactAlignment, outcomeMethod) => {
    if (!Array.isArray(impactAlignment) || !outcomeMethod) {
        return [];
    }

    if (outcomeMethod === "Programme Custom") {
        return impactAlignment.map((objective, index) => ({
            name: objective?.name,
            objectiveUNSDG: Array.isArray(objective?.objectiveUNSDG) ? objective.objectiveUNSDG : [],
            count: typeof objective?.count === "number" ? objective.count : 0,
            objectivesOutcome: Array.isArray(objective?.outcomes)
                ? objective.outcomes.map((outcome, idx) => ({
                    name: outcome?.name || `Outcome ${idx + 1}`,
                    outcomeDescription: outcome?.outcomeDescription,
                    outcomeUNSDG: Array.isArray(outcome?.outcomeUNSDG) ? outcome.outcomeUNSDG : [],
                    count: typeof outcome?.count === "number" ? outcome.count : 0,
                }))
                : [],
        }));
    } else if (outcomeMethod === "Opportunity Custom2") {
        return impactAlignment.map((objective, index) => ({
            name: objective?.name,
            objectiveUNSDG: Array.isArray(objective?.objectiveUNSDG) ? objective.objectiveUNSDG : [],
            count: typeof objective?.count === "number" ? objective.count : 0,
            outcomeDescription: objective?.outcomeDescription,
        }));
    }
    return [];
};

const getUniqueUnsdgKeys = (impactAlignment, outcomeMethod) => {
    if (!Array.isArray(impactAlignment)) return [];
    const unsdgKeys = new Set();

    impactAlignment.forEach((objective) => {
        if (Array.isArray(objective?.objectiveUNSDG)) {
            objective.objectiveUNSDG.forEach((key) => unsdgKeys.add(key));
        }
        if (outcomeMethod === "Programme Custom" && Array.isArray(objective?.objectivesOutcome)) {
            objective.objectivesOutcome
                .forEach((outcome) => {
                    if (Array.isArray(outcome?.outcomeUNSDG)) {
                        outcome.outcomeUNSDG.forEach((key) => unsdgKeys.add(key));
                    }
                });
        }
    });

    return Array.from(unsdgKeys);
};

const ImpactAlignment = ({ className, impactAlignment, outcomeMethod }) => {
    const intl = useIntl();
    const [selectedUnsdgs, setSelectedUnsdgs] = useState([]);
    const [selectedName, setSelectedName] = useState("");
    const maxValue = getMaxCount(impactAlignment);
    const objectives = processImpactAlignment(impactAlignment, outcomeMethod);

    const uniqueUnsdgKeys = getUniqueUnsdgKeys(objectives, outcomeMethod);
    const filteredGoals = goals.filter((goal) => uniqueUnsdgKeys.includes(goal.key));

    const extractAndGroupObjectivesOutcome = (data) => {
        if (!Array.isArray(data)) {
            return [];
        }

        return data.map(({ name: title, objectivesOutcome, objectiveUNSDG, count }, index) => ({
            key: index,
            label: (
                <div className="objectiveHeader">
                    <div className="objectiveTextContainer">
                        <StyledText as="p" variant="B3d">{title || "Untitled Objective"}</StyledText>
                    </div>
                    <div className="graphContainer">
                        <HorizontalGraph
                            percentage={typeof count === "number" ? count : 0}
                            maxValue={maxValue}
                            background="#04ac9c"
                        />
                    </div>
                </div>
            ),
            children:
                Array.isArray(objectivesOutcome) && objectivesOutcome.length > 0
                    ? objectivesOutcome.map(({ name, outcomeDescription, outcomeApproach, outcomeUNSDG, count }) => (
                        <div key={name || `outcome-${index}`} className="collapseChildWrapper">
                            <div className="objectiveTextContainer">
                                <button
                                    type="button"
                                    className={`collapseChildContainer ${selectedName === name ? "selectedName" : ""}`}
                                    onClick={() => {
                                        setSelectedUnsdgs([
                                            ...(Array.isArray(objectiveUNSDG) ? objectiveUNSDG : []),
                                            ...(Array.isArray(outcomeUNSDG) ? outcomeUNSDG : []),
                                        ]);
                                        setSelectedName(name || "");
                                    }}
                                >
                                    <StyledText as="p" variant="B3d">{name || "Untitled Outcome"}</StyledText>
                                    <StyledText as="p" variant="B3">{outcomeApproach || outcomeDescription}</StyledText>
                                </button>
                            </div>
                            <div className="graphContainer">
                                <HorizontalGraph
                                    percentage={typeof count === "number" ? count : 0}
                                    maxValue={maxValue}
                                    background="#bcfbf5"
                                />
                            </div>
                        </div>
                    ))
                    : null,
        }));
    };

    const renderOpportunityCustom2 = (data) => {
        if (!Array.isArray(data)) {
            return null;
        }

        return data.map(({ name, objectiveUNSDG, count, outcomeDescription }, index) => (
            <div key={name || `objective-${index}`} className="collapseChildWrapper opportunityCustom2">
                <div className="objectiveTextContainer">
                    <button
                        type="button"
                        className={`collapseChildContainer ${selectedName === name ? "selectedName" : ""}`}
                        onClick={() => {
                            setSelectedUnsdgs([...(Array.isArray(objectiveUNSDG) ? objectiveUNSDG : [])]);
                            setSelectedName(name || "");
                        }}
                    >
                        <StyledText as="p" variant="B3d">{name || "Untitled Objective"}</StyledText>
                        <StyledText as="p" variant="B3">{outcomeDescription}</StyledText>
                    </button>
                </div>
                <div className="graphContainer">
                    <HorizontalGraph
                        percentage={typeof count === "number" ? count : 0}
                        background="#04ac9c"
                    />
                </div>
            </div>
        ));
    };

    const impactStrategyOutcomes = extractAndGroupObjectivesOutcome(objectives);

    return (
        <div className={className}>
            <div className="unsdgPartitionContainer">
                <div className="unsdgsContainer">
                    <button
                        className="showAllGoals"
                        onClick={() => {
                            setSelectedUnsdgs([]);
                            setSelectedName("");
                        }}
                    >
                        <div>
                            <CheckOutlined />
                        </div>
                        <StyledText as="p" variant="B3">{intl.formatMessage({ id: "show_all_goals" })}</StyledText>
                    </button>
                    {filteredGoals.map((el) => (
                        <Avatar
                            key={el.image}
                            src={Array.isArray(selectedUnsdgs) && selectedUnsdgs.includes(el.key) ? el.selected : el.image}
                            shape="square"
                            className="unsdgs"
                        />
                    ))}
                </div>
                <div className="collapseContainer">
                    {outcomeMethod === "Programme Custom" ? (
                        <Collapse
                            items={Array.isArray(impactStrategyOutcomes) ? impactStrategyOutcomes : []}
                            expandIconPosition="end"
                        />
                    ) : (
                        renderOpportunityCustom2(objectives)
                    )}
                </div>
            </div>
        </div>
    );
};

const StyledImpactAlignment = styled(ImpactAlignment)`
  &.impactAlignment {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .horizontalGraphContainer {
      margin-top: 0;
    }

    .unsdgPartitionContainer {
      display: flex;
      gap: 20px;
      background: white;
      padding: 30px;
      border-radius: 10px;

      .collapseContainer {
        width: 50%;
        button {
          width: 100%;
        }
      }

      .unsdgsContainer {
        display: flex;
        gap: 15px;
        width: 48%;
        flex-wrap: wrap;
        height: max-content;

        .showAllGoals {
          height: 100px;
          width: 100px;
          background: white;
          display: flex;
          flex-direction: column;
          padding: 15px;
          justify-content: center;
          align-items: center;
          text-align: center;
          border: 2px solid #00877a;
          border-radius: 6px;

          p,
          svg {
            color: #00877a !important;
          }
        }

        .unsdgs {
          height: 100px;
          width: 100px;
        }
      }
    }

    .expandAll {
      color: #00877a;
      text-align: end;
      background: transparent;
      border: 0;
      cursor: pointer;
    }

    .ant-collapse {
      border: 0;
      border-bottom: 1px solid #d7dde1;
      .ant-collapse-header {
        background: white !important;
      }
    }

    .ant-collapse-content-active {
      border-top: 0;
    }

    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg {
        fill: #313131 !important;
      }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }

    .objectiveHeader {
      display: flex;
      gap: 20px;
      align-items: center;
      width: 100%;
    }

    .objectiveTextContainer {
      flex: 0 0 40%;
      max-width: 40%;
    }

    .collapseChildWrapper {
      display: flex;
      gap: 10px;
      align-items: center;
      width: 100%;

      &.opportunityCustom2 {
        flex-direction: row;
        align-items: center;
        margin-bottom: 10px;
      }
    }

    .collapseChildContainer {
      border: 1px solid #d7dde1;
      padding: 12px 16px;
      cursor: pointer;
      text-align: left;
      background: white;
      font-size: 14px;
      font-weight: 600;
      color: #242424;
      font-family: "Poppins", sans-serif;
      width: 100%;

      &.selectedName {
        border: 1px solid #04ac9c;
        border-radius: 6px;
        background: #bcfbf5;
      }
    }

    .graphContainer {
      width: 400px;
    }
  }
`;

export default StyledImpactAlignment;